import Types from '../../constants/ActionTypes';

import distance from '../../utils/distance';

/**
 * appointmentModal options: we only have one of these at a time
 * showSelect // modal to select an appointment
 * showCancel // the modal to confirm the cancellation of a reservation
 * showChange // the modal to change a reservation
 * showSelectEditCancel // the modal for selecting one of multiple future reservations
 * showReservationConfirmed // the modal to show the confirmation of a new reservation
 * showScreenTefapAttestation // the screen-level tefap attestation modal (mobile only)
 * showScreenMissingInfo // the screen-level missing info modal
 */

export const FILTER_ALLOW_ALL = {
  program_type: ['non-food', 'foodpantry', 'soupkitchen'],
  status: ['opennow', 'opentoday', 'openthisweek', 'other'],
  pantry_type: ['plentifulpantry', 'verifiedpartnerpantry', 'unverifiedpartnerpantry'],
  service_type: ['line', 'reservation', 'pre-registration', 'qr-code'],
};

const initialState = {
  _loadedPantries: [], // all pantries returned by the API - used internally to support client-side-searching
  pantries: [], // list of pantries - will always have the search term applied
  filter: FILTER_ALLOW_ALL, // the filter that is applied to the pantries list
  pantry: {}, // selected pantry
  visits: [], // list of visits
  visit: {}, // current visit (new, edit)
  navigateTo: null,
  location: {
    currentLocation: {
      coords: {
        latitude: 40.7128,
        longitude: -74.006,
      },
    },
  },
  showMustVerify: false,
  showMustLogin: false,
  appointmentModal: null,
  editCancelVisits: {
    // if there are more than one upcoming visits
    editOrCancel: '', // edit or cancel
    visits: [], // the options from which one can select
  },
  updates: [],
  update_thread: {}, // id, updates
  refresh: false,
  lastApiCall: 0, // make sure we don't call too often
  pantryRegion: {}, // pantry search boundaries, defined by lat/lng and deltas

  // Search mode (global Algolia search)
  searchResults: [],
  loadingSearchResults: false,
  searchResultsError: null,
  searchQuery: '',
  searchTotalResults: 0,
  searchHasMore: false,
  searchExecutedAt: null,

  // Display mode control
  displayMode: 'explore', // 'explore' | 'search'
};

export default function pantry(state = initialState, action = {}) {
  function refreshPantryVisits(selectedVisitId) {
    if (state.visits && state.visits.length) {
      state.visits.forEach((visit) => {
        if (visit.pantry.id === state.pantry.pantry_id) {
          state.pantry.visits.push(visit);
        }
        // use the data from visits list
        // rather than the individual visit which is incomplete
        if (visit.id === selectedVisitId) {
          state.visit = visit;
        }
      });
    }
  }

  switch (action.type) {
    case Types.LOAD_PANTRIES:
      const pantriesFromApi = action.data.pantries || [];
      const pantryRegion = action.data.pantryRegion;

      // calculate distance from user location in miles
      let latitude, longitude;
      if (state.location.currentLocation) {
        latitude = state.location.currentLocation.coords.latitude;
        longitude = state.location.currentLocation.coords.longitude;
      }
      if (latitude && longitude) {
        pantriesFromApi.forEach((pantry) => {
          pantry.distance = distance(pantry.latitude, pantry.longitude, latitude, longitude);
        });
      }

      return Object.assign({}, state, {
        _loadedPantries: pantriesFromApi,
        pantries: pantriesFromApi,
        lastApiCall: new Date().getTime(),
        pantryRegion,
      });

    case Types.SET_LOCATION:
      state.location = action.data;
      if (state.pantries && state.pantries.length && state.location.currentLocation) {
        const latitude = state.location.currentLocation.coords.latitude;
        const longitude = state.location.currentLocation.coords.longitude;
        state.pantries.forEach((pantry) => {
          pantry.distance = distance(pantry.latitude, pantry.longitude, latitude, longitude);
        });
      }
      return Object.assign({}, state);

    case Types.SHOW_PANTRY_DETAILS:
    case Types.RESERVATIONS_QUEUE:
      state.pantry = action.data;
      state.pantry.visits = [];
      if (state.visits && state.visits.length) {
        state.visits.forEach((visit) => {
          if (visit.pantry.id == state.pantry.pantry_id) {
            state.pantry.visits.push(visit);
          }
        });
      }
      if (state.pantry && state.pantry.nextVisit) {
        state.visit = state.pantry.nextVisit;
      }
      if (state.location.currentLocation) {
        const latitude = state.location.currentLocation.coords.latitude;
        const longitude = state.location.currentLocation.coords.longitude;
        state.pantry.distance = distance(state.pantry.latitude, state.pantry.longitude, latitude, longitude);
      }
      return Object.assign({}, state);

    case Types.CLOSE_PANTRY_DETAILS:
      state.pantry = {};
      state.visit = {};
      state.appointmentModal = null;
      return Object.assign({}, state);

    case Types.OPEN_MAKE_APPOINTMENT:
      state.appointmentModal = 'showSelect';
      return Object.assign({}, state);

    case Types.CLOSE_MAKE_APPOINTMENT:
      state.appointmentModal = null;
      return Object.assign({}, state);

    case Types.OPEN_MUST_VERIFY:
      state.showMustVerify = true;
      return Object.assign({}, state);

    case Types.CLOSE_MUST_VERIFY:
      state.showMustVerify = false;
      return Object.assign({}, state);

    case Types.OPEN_MUST_LOGIN:
      state.showMustLogin = true;
      return Object.assign({}, state);

    case Types.CLOSE_MUST_LOGIN:
      state.showMustLogin = false;
      return Object.assign({}, state);

    case Types.OPEN_CHANGE_APPOINTMENT:
      state.appointmentModal = 'showChange';
      state.pantry.edit_options = action.data;
      return Object.assign({}, state);

    case Types.CLOSE_CHANGE_APPOINTMENT:
      state.appointmentModal = null;
      return Object.assign({}, state);

    case Types.OPEN_CANCEL_APPOINTMENT:
      state.visit = Object.assign({}, action.data);
      state.appointmentModal = 'showCancel';
      return Object.assign({}, state);

    case Types.CLOSE_CANCEL_APPOINTMENT:
      state.appointmentModal = null;
      return Object.assign({}, state);

    case Types.CLOSE_RESERVATION_CONFIRMED:
      state.appointmentModal = null;
      return Object.assign({}, state);

    case Types.SET_PANTRY_FILTER: {
      const { filter } = action.data;
      return Object.assign({}, state, { filter });
    }

    case Types.OPEN_SCREEN_TEFAP_ATTESTATION:
      state.appointmentModal = 'showScreenTefapAttestation';
      return Object.assign({}, state);

    case Types.CLOSE_SCREEN_TEFAP_ATTESTATION:
      state.appointmentModal = null;
      return Object.assign({}, state);

    case Types.OPEN_SCREEN_MISSING_INFO:
      state.appointmentModal = 'showScreenMissingInfo';
      return Object.assign({}, state);

    case Types.CLOSE_SCREEN_MISSING_INFO:
      state.appointmentModal = null;
      return Object.assign({}, state);

    case Types.CONFIRM_CANCEL_APPOINTMENT:
      state.appointmentModal = null;
      // update visits list
      state.visits = action.data;
      // update visits of current pantry
      state.pantry.visits = [];
      if (state.visits && state.visits.length) {
        state.visits.forEach((visit) => {
          if (visit.pantry.id == state.pantry.pantries_id) {
            state.pantry.visits.push(visit);
          }
        });
      }
      // update visits of pantries in list
      const newPantries = [];
      state.pantries.forEach((pantry) => {
        pantry.visits = [];
        state.visits.forEach((visit) => {
          if (visit.pantry.id == pantry.id) {
            pantry.visits.push(visit);
          }
        });
        newPantries.push(pantry);
      });
      state.pantries = newPantries;
      return Object.assign({}, state);

    case Types.GET_VISITS:
      state.visits = action.data;
      state.lastApiCall = new Date().getTime();
      return Object.assign({}, state);

    case Types.MAKE_APPOINTMENT:
      state.visits = action.data.visits;
      state.appointmentModal = 'showReservationConfirmed';
      state.pantry.visits = [];
      refreshPantryVisits(action.data.visit.id);
      return Object.assign({}, state);

    case Types.SELECT_VISIT:
      state.pantry = action.data.pantry;
      state.pantry.visits = [];
      refreshPantryVisits(action.data.visit.id);
      return Object.assign({}, state);

    case Types.OPEN_SELECT_EDIT_CANCEL:
      state.editCancelVisits = action.data;
      state.appointmentModal = 'showSelectEditCancel';
      return Object.assign({}, state);

    case Types.CLOSE_SELECT_EDIT_CANCEL:
      state.editCancelVisits.visits = [];
      state.appointmentModal = null;
      return Object.assign({}, state);

    case Types.SELECT_EDIT_CANCEL_VISIT:
      state.visit = action.data.visit;
      state.pantry.edit_options = action.data.options;
      if (state.editCancelVisits.editOrCancel == 'edit') {
        state.appointmentModal = 'showChange';
      } else {
        state.appointmentModal = 'showCancel';
      }
      return Object.assign({}, state);

    case Types.UPDATE_VISIT:
      state.visit = action.data;
      return Object.assign({}, state);

    case Types.GET_UPDATES:
      state.updates = action.data;
      state.update_thread = {};
      state.lastApiCall = new Date().getTime();
      return Object.assign({}, state);

    case Types.GET_THREAD:
      state.update_thread = action.data;
      return Object.assign({}, state);

    case Types.CLOSE_THREAD:
      state.update_thread = {};
      return Object.assign({}, state);

    case Types.REFRESH:
      state.refresh = !state.refresh;
      return Object.assign({}, state);

    // Search pantries actions
    case Types.SEARCH_PANTRIES_REQUEST:
      return Object.assign({}, state, {
        loadingSearchResults: true,
        searchResultsError: null,
        displayMode: 'search',
      });

    case Types.SEARCH_PANTRIES_SUCCESS:
      return Object.assign({}, state, {
        loadingSearchResults: false,
        searchResults: action.data.results || [],
        searchTotalResults: action.data.total || 0,
        searchHasMore: action.data.total > (action.data.results || []).length,
        searchQuery: action.data.query || '',
        searchExecutedAt: new Date().toISOString(),
      });

    case Types.SEARCH_PANTRIES_FAILURE:
      return Object.assign({}, state, {
        loadingSearchResults: false,
        searchResultsError: action.data.error || 'Search failed',
      });

    case Types.CLEAR_SEARCH:
      return Object.assign({}, state, {
        displayMode: 'explore',
        searchResults: [],
        searchQuery: '',
        searchTotalResults: 0,
        searchHasMore: false,
        searchResultsError: null,
        searchExecutedAt: null,
      });

    case Types.PANTRY_LOGOUT:
      return Object.assign({}, initialState);

    default:
      return Object.assign({}, state);
  }
}
